import React, { useState } from "react";
import { ShoppingCart, ChefHat } from "lucide-react";
import MenuItemCard from "./MenuItemCard";

const MenuSection = ({
  selectedCategory,
  menuItems,
  categories,
  handleAddToCart,
  addingToCart,
  handleFavoriteToggle,
  handleCategoryChange,
  favorites,
  cartItems = [],
  updateQuantity,
  removeItem,
  searchTerm,
  setSearchTerm,
}) => {
  const [typeFilter, setTypeFilter] = useState("all"); // all, veg, non-veg

  // Filter menu items by veg/non-veg and search term
  // Only show items that are available or unavailable (for overlay logic)
  const filteredMenuItems = menuItems.filter((item) => {
    // Only include items with available === true or available === false (for overlay)
    // If you want to hide unavailable items, use: return item.available === true;
    // But for overlay, we show all and let MenuItemCard handle the overlay
    if (typeFilter !== "all") {
      if (typeFilter === "veg" && (item.type || "veg").toLowerCase() !== "veg")
        return false;
      if (
        typeFilter === "non-veg" &&
        (item.type || "veg").toLowerCase() !== "non-veg"
      )
        return false;
    }
    if (searchTerm && searchTerm.trim() !== "") {
      return (item.name || "")
        .toLowerCase()
        .includes(searchTerm.trim().toLowerCase());
    }
    return true;
  });

  if (selectedCategory) {
    return (
      <>
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {selectedCategory.charAt(0).toUpperCase() +
                  selectedCategory.slice(1)}
              </h2>
              <p className="text-gray-600 flex items-center">
                <span className="mr-2">🍴</span>
                {filteredMenuItems.length} delicious item
                {filteredMenuItems.length !== 1 ? "s" : ""} available
              </p>
            </div>
            {/* Veg/Non-Veg Filter Buttons and Search */}
            <div className="flex gap-2 items-center mt-2 sm:mt-0 flex-wrap">
              <button
                className={`px-3 py-1 rounded-lg border text-sm font-medium flex items-center gap-1 transition-all ${
                  typeFilter === "all"
                    ? "bg-primary-600 text-white border-primary-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                }`}
                onClick={() => setTypeFilter("all")}
              >
                All
              </button>
              <button
                className={`px-3 py-1 rounded-lg border text-sm font-medium flex items-center gap-1 transition-all ${
                  typeFilter === "veg"
                    ? "bg-green-600 text-white border-green-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                }`}
                onClick={() => setTypeFilter("veg")}
              >
                <span className="inline-block w-3 h-3 rounded-sm bg-green-600 border border-green-800"></span>{" "}
                Veg
              </button>
              <button
                className={`px-3 py-1 rounded-lg border text-sm font-medium flex items-center gap-1 transition-all ${
                  typeFilter === "non-veg"
                    ? "bg-red-600 text-white border-red-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                }`}
                onClick={() => setTypeFilter("non-veg")}
              >
                <span className="inline-block w-3 h-3 rounded-sm bg-red-600 border border-red-800"></span>{" "}
                Non Veg
              </button>
              {/* Search Input */}
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search menu items by name..."
                className="ml-4 w-56 px-3 py-1.5 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                style={{ minWidth: 180 }}
              />
            </div>
          </div>
        </div>

        {filteredMenuItems.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6 justify-items-center">
            {filteredMenuItems.map((item, index) => (
              <MenuItemCard
                key={item.id || `${selectedCategory}-${index}`}
                item={item}
                onAddToCart={handleAddToCart}
                addingToCart={addingToCart}
                handleFavoriteToggle={handleFavoriteToggle}
                favorites={favorites}
                showType={true}
                cartItems={cartItems}
                updateQuantity={updateQuantity}
                removeItem={removeItem}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-20 px-6 bg-white rounded-3xl shadow-lg border border-gray-100">
            <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-8 shadow-inner">
              <ShoppingCart className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              No Items Available
            </h3>
            <p className="text-gray-600 max-w-md mx-auto mb-8 leading-relaxed">
              We're currently updating our menu for this category. Please
              explore other categories or check back later for exciting new
              additions.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              {categories
                .filter((cat) => cat !== selectedCategory)
                .slice(0, 3)
                .map((cat) => (
                  <button
                    key={cat}
                    onClick={() => handleCategoryChange(cat)}
                    className="px-6 py-3 bg-gradient-to-r from-primary-50 to-primary-100 hover:from-primary-100 hover:to-primary-200 text-primary-700 rounded-xl font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    Browse {cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </button>
                ))}
            </div>
          </div>
        )}
      </>
    );
  }
  return (
    <div className="text-center py-20 px-6 bg-white rounded-3xl shadow-lg border border-gray-100">
      <div className="w-24 h-24 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg">
        <ChefHat className="w-12 h-12 text-primary-600" />
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-4">
        Select a Category
      </h3>
      <p className="text-gray-600 max-w-md mx-auto leading-relaxed">
        Choose a category from the sidebar to explore our delicious menu items.
      </p>
    </div>
  );
};
export default MenuSection;
